<!-- eslint-disable no-use-before-define -->
<script setup>
import { CsButton } from '../button';
import { CsIcon } from '../icon';
import { computed, ref, watch } from 'vue';
import { CsTeleport } from '../teleport';
import { CsTransitionChild, CsTransitionRoot } from '../transition';

/**
 * @typedef {object} ImageItem
 * @property {string|number} id - Unique identifier for the image
 * @property {string} src - Image source URL
 * @property {string} [alt] - Alt text for accessibility
 */

/**
 * @typedef {object} ImagePreviewProps
 * @property {boolean} [isOpen=false] - Controls whether the image preview is displayed
 * @property {ImageItem[]} [images=[]] - Array of images to display
 * @property {string|number} [selectedImageId] - ID of the currently selected image
 * @property {boolean} [isCloseOnBackdropClick=true] - Whether clicking the backdrop closes the preview
 * @property {boolean} [isCloseOnEscape=true] - Whether pressing Escape key closes the preview
 * @property {boolean} [showNavigation=true] - Whether to show navigation arrows
 * @property {number} [zIndex=1000] - Z-index for the modal overlay
 */

/**
 * @type {ImagePreviewProps}
 */
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  images: {
    type: Array,
    default: () => [],
  },
  selectedImageId: {
    type: [String, Number],
    default: null,
  },
  isCloseOnBackdropClick: {
    type: Boolean,
    default: true,
  },
  isCloseOnEscape: {
    type: Boolean,
    default: true,
  },
  showNavigation: {
    type: Boolean,
    default: true,
  },
  zIndex: {
    type: Number,
    default: 1000,
  },
});

const emit = defineEmits(['close', 'image-change']);

const currentImageIndex = ref(0);
const imagePreviewRef = ref(null);

const currentImage = computed(() => {
  return props.images[currentImageIndex.value] || null;
});

const hasPrevious = computed(() => currentImageIndex.value > 0);
const hasNext = computed(
  () => currentImageIndex.value < props.images.length - 1
);

function onClose() {
  emit('close');
}

function onBackdropClick() {
  if (props.isCloseOnBackdropClick) {
    onClose();
  }
}


function onArrowKey(event) {
  event.stopPropagation();
  event.preventDefault();

  if (event.key === 'ArrowLeft') {
    goToPrevious();
  } else if (event.key === 'ArrowRight') {
    goToNext();
  }
}

function goToPrevious() {
  if (hasPrevious.value) {
    // eslint-disable-next-line no-plusplus
    currentImageIndex.value--;
    emit('image-change', currentImage.value);
  }
}

function goToNext() {
  if (hasNext.value) {
    // eslint-disable-next-line no-plusplus
    currentImageIndex.value++;
    emit('image-change', currentImage.value);
  }
}

function onKeydown(event) {
  // Prevent tab from escaping the modal (basic focus lock)
  if (event.key === 'Tab') {
    event.preventDefault();
    return;
  }

  if (props.isCloseOnEscape && event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    onClose();
  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
    onArrowKey(event);
  }
}

function registerEventListeners() {
  if (imagePreviewRef.value) {
    imagePreviewRef.value.addEventListener('keydown', onKeydown);
  }
}

function unregisterEventListeners() {
  if (imagePreviewRef.value) {
    imagePreviewRef.value.removeEventListener('keydown', onKeydown);
  }
}

function focusImagePreview() {
  if (imagePreviewRef.value) {
    imagePreviewRef.value.focus();
  }
}

function enableBodyScrollLock() {
  document.body.style.overflow = 'hidden';
}

function disableBodyScrollLock() {
  document.body.style.overflow = 'auto';
}

// Set initial image index based on selectedImageId
watch(
  () => props.selectedImageId,
  newId => {
    if (newId && props.images.length > 0) {
      const index = props.images.findIndex(img => img.id === newId);
      if (index >= 0) {
        currentImageIndex.value = index;
      }
    }
  },
  { immediate: true }
);

// Register event listeners and focus management when the preview is opened
watch(
  () => props.isOpen,
  newValue => {
    if (newValue) {
      enableBodyScrollLock();
      // Use nextTick to ensure DOM is updated before focusing
      setTimeout(() => {
        registerEventListeners();
        focusImagePreview();
      }, 50);
    } else {
      disableBodyScrollLock();
      unregisterEventListeners();
    }
  }
);
</script>

<template>
  <CsTeleport>
    <CsTransitionRoot
      :is-open="isOpen"
      data-component="CsImagePreview"
      data-slot="root"
      role="dialog"
      class="relative"
      :style="{ zIndex: props.zIndex }"
    >
      <!-- Backdrop -->
      <CsTransitionChild
        enter-active-class="transition ease-out duration-300"
        enter-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition ease-in duration-200"
        leave-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          data-slot="backdrop"
          class="fixed inset-0 bg-[rgba(0,0,0,.9)] transition-opacity"
          aria-hidden="true"
          @click="onBackdropClick"
        />
      </CsTransitionChild>

      <!-- Content -->
      <CsTransitionChild
        enter-active-class="transition ease-out duration-300"
        enter-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition ease-in duration-200"
        leave-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          ref="imagePreviewRef"
          tabindex="-1"
          class="pointer-events-none fixed inset-0 z-50 h-screen w-screen outline-none"
          role="dialog"
          aria-label="Image preview"
        >
          <div class="relative h-screen w-full p-4">
            <!-- Main Image Area -->
            <div
              class="pointer-events-none flex h-screen flex-1 items-center justify-center p-8"
            >
              <div
                v-if="currentImage"
                class="relative max-h-[70vh] overflow-hidden rounded-lg"
              >
                <div class="pointer-events-auto absolute right-2 top-2">
                  <!-- Close Button -->
                  <CsButton
                    variant="secondary"
                    size="sm"
                    is-icon-only
                    @click="onClose"
                  >
                    <CsIcon name="x" />
                  </CsButton>
                </div>

                <img
                  :src="currentImage.src"
                  :alt="currentImage.alt || 'Preview image'"
                  class="pointer-events-auto max-h-[70vh] max-w-full rounded-lg object-contain shadow-2xl"
                />
              </div>
            </div>

            <!-- Fixed Thumbnail Navigation -->
            <div
              v-if="props.images.length > 1"
              class="absolute bottom-0 left-0 right-0 z-50 flex w-full items-center justify-center pb-8"
            >
              <div class="pointer-events-auto flex items-center gap-2">
                <button
                  v-for="(image, index) in props.images"
                  :key="image.id"
                  class="h-12 w-12 overflow-hidden rounded-lg transition-opacity"
                  :class="[
                    index === currentImageIndex
                      ? 'ring-2 ring-white ring-offset-2 ring-offset-black/50'
                      : `
                        opacity-60
                        hover:opacity-80
                      `,
                  ]"
                  @click="currentImageIndex = index"
                >
                  <img
                    :src="image.src"
                    :alt="image.alt || 'Thumbnail'"
                    class="h-12 w-12 object-cover"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </CsTransitionChild>
    </CsTransitionRoot>
  </CsTeleport>
</template>
